// stream-sign — dedup single-flight do `POST /v1/stream/sign`.
//
// Problema medido na LG real (voltando da live pra Home): SETE chamadas de
// `/stream/sign` DUPLICADAS e simultâneas pro MESMO streamId. Origem: N
// componentes independentes (LivePip, probe do toast de lembrete, player)
// pedindo a URL do mesmo canal ao mesmo tempo. Cada uma abre uma conexão →
// satura o pool do Chr38/53 → o sign que importa entra na fila.
//
// Fix: uma ÚNICA chamada por streamId em voo (promise compartilhada), com um
// cache curto por chave invalidado pela expiração da própria URL assinada
// (`SignedStreamUrl.expiresAt`). N pedidos simultâneos ⇒ 1 fetch.
//
// A janela CRÍTICA (net-priority) NÃO é marcada aqui: ela vive dentro de
// `api.signStream`, o funil por onde TODO sign passa — inclusive os players de
// VOD/série/catchup, que não usam este dedup. Consequência boa e intencional:
// um cache hit daqui não abre janela crítica, porque não há rede nenhuma.

import type { SignedStreamUrl } from '@app-iptv/iptv-client'

import { api } from './api'

type SignType = 'live' | 'vod' | 'series' | 'catchup'
interface SignExtras {
  ext?: string
  start?: number
  duration?: number
  avoidEndpointId?: string
}

// Margem de segurança: nunca reusar um sign que expira em menos disso. Cobre o
// tempo entre pegar a URL e o player abrir a conexão de fato.
const SAFETY_MARGIN_MS = 15_000
// Teto duro de reuso: mesmo que o backend assine com validade longa, não
// reusamos uma URL de live além disso — limita "staleness" (canal que caiu, re-
// checagem de trial/cobertura server-side, etc). Curto o bastante pra ser seguro,
// longo o bastante pra colapsar a rajada de aberturas da Home.
const MAX_REUSE_MS = 45_000

interface CacheEntry {
  value: SignedStreamUrl
  reuseUntilMs: number
}

const inflight = new Map<string, Promise<SignedStreamUrl>>()
const cache = new Map<string, CacheEntry>()

function keyOf(type: SignType, streamId: string, extras: SignExtras): string {
  // Ordem estável dos campos → chave determinística. avoidEndpointId entra na
  // chave: um re-sign de failover (evitar endpoint X) NÃO deve casar com o sign
  // normal do mesmo canal.
  return [
    type,
    streamId,
    extras.ext ?? '',
    extras.start ?? '',
    extras.duration ?? '',
    extras.avoidEndpointId ?? '',
  ].join('|')
}

/** Normaliza expiresAt pra epoch-ms. Backend pode mandar em segundos (valores
 *  < 1e12) ou já em ms — detecta pela magnitude. */
function expiresAtMs(value: number): number {
  return value < 1e12 ? value * 1000 : value
}

/**
 * Assina um stream com dedup single-flight + cache curto por chave.
 *
 * - Requests simultâneos pra mesma chave compartilham UMA promise (o fix do bug
 *   dos 7 duplicados).
 * - Uma resolução recente é reusada até perto da expiração da URL (cache).
 * - `avoidEndpointId` (failover de endpoint) SEMPRE ignora o cache de leitura:
 *   failover precisa de resolução autoritativa do backend. O in-flight ainda é
 *   compartilhado (dois failovers idênticos simultâneos = 1 fetch).
 */
export function signStreamDeduped(
  type: SignType,
  streamId: string,
  extras: SignExtras = {},
  // Repassado pro funil `api.signStream`. `critical: false` = sign de background
  // (probe do lembrete) que não vira vídeo — não deve abrir janela crítica.
  //
  // LIMITAÇÃO CONHECIDA do single-flight: `keyOf` NÃO inclui `opts`, então um
  // probe (`critical:false`) em voo e um play (`critical:true`) pro mesmo canal
  // colapsam na mesma promise — e o play herda o `critical:false` do probe, sem
  // abrir janela crítica.
  //
  // Isso NÃO é exótico: é o fluxo lembrete → "Espiar" por desenho (o toast faz
  // probe do canal X, o usuário aceita e o PiP dá play no MESMO canal X, dentro
  // da janela do probe).
  //
  // O que a janela protege não é o sign contra OUTROS signs — é o par
  // sign+buffering contra a TELEMETRIA. Então a consequência real de herdar
  // `critical:false` é: a telemetria represada pode sair durante o buffering
  // inicial desse play.
  //
  // Aceitável pelo TAMANHO da exposição, não por ausência de disputa: a herança
  // só vale enquanto o probe está em voo (centenas de ms) e o play afetado é um
  // preview de PiP, não o player principal em tela cheia. Depois que o probe
  // resolve, o play vem do cache (zero rede — janela desnecessária de qualquer
  // forma). Se um dia o custo aparecer na medição, o fix é `opts` na chave (ao
  // preço de perder o dedup entre probe e play).
  opts: { critical?: boolean } = {},
): Promise<SignedStreamUrl> {
  const key = keyOf(type, streamId, extras)
  const bypassCache = extras.avoidEndpointId != null

  if (!bypassCache) {
    const hit = cache.get(key)
    if (hit && Date.now() < hit.reuseUntilMs) {
      return Promise.resolve(hit.value)
    }
    if (hit) cache.delete(key) // expirado
  }

  const pending = inflight.get(key)
  if (pending) return pending

  // try/finally (construção de linguagem) em vez de Promise.prototype.finally:
  // `.finally` é Chr63+, ABAIXO do piso da TV (Chr38/53). O IIFE async suspende
  // no 1º await (api.signStream), então o `inflight.set` abaixo roda ANTES da
  // resolução — mesma semântica de single-flight, sem depender de polyfill.
  const promise = (async (): Promise<SignedStreamUrl> => {
    try {
      const value = await api.signStream(type, streamId, extras, opts)
      if (!bypassCache) {
        const reuseUntilMs = Math.min(
          expiresAtMs(value.expiresAt) - SAFETY_MARGIN_MS,
          Date.now() + MAX_REUSE_MS,
        )
        if (reuseUntilMs > Date.now()) {
          cache.set(key, { value, reuseUntilMs })
        }
      }
      return value
    } finally {
      inflight.delete(key)
    }
  })()

  inflight.set(key, promise)
  return promise
}

/** Invalida o cache de sign de um streamId (todas as chaves derivadas dele).
 *  Chamar quando a URL falhou/ficou bloqueada pra forçar re-sign no próximo play. */
export function invalidateStreamSign(streamId: string): void {
  const segment = `|${streamId}|`
  for (const key of cache.keys()) {
    // key = `${type}|${streamId}|ext|start|duration|avoid` — o streamId é o 2º
    // segmento, sempre cercado por pipes. streamId Xtream é numérico (sem `|`).
    if (key.includes(segment)) cache.delete(key)
  }
}

/** Reset pra testes. */
export function __resetStreamSignForTests(): void {
  inflight.clear()
  cache.clear()
}
